import { useCallback, useMemo, useState } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import type { FilterConfig, FilterValue, SelectValue, FilterNegations } from '@/types/filters.types';
import { isDateRange, isSelect } from '@/types/filters.types';
import { isDateRangePresetKey, resolvePreset } from '@/lib/dateRangePresets';
import { useEffectiveFacilityId, useEffectiveFacilityIds, useIsFacilityLocked } from '@/hooks/useGlobalFacility';
import { leadSourceFilterConfig } from '@/hooks/filters/leadSourceOptions';

/**
 * Filter keys MUST match the canonical legacy filter registry.
 * DO NOT rename keys or change option values — dynamic lists store these permanently.
 * See lib/filterRegistry.ts for the full registry and rules.
 */
export const PATIENT_DASHBOARD_FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search patients...' },
  { key: 'membership-status', label: 'Status', type: 'select', allLabel: 'All statuses', multi: true },
  leadSourceFilterConfig('Referral Source', 'All sources'),
  { key: 'coordinator', label: 'Coordinator', type: 'select', allLabel: 'All coordinators', multi: true },
  { key: 'tags', label: 'Tag', type: 'select', allLabel: 'All tags', multi: true },
  { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
  { key: 'daterange', label: 'Created Date', type: 'daterange' },
  { key: 'dob', label: 'Date of Birth', type: 'daterange' },
];

export const FORMS_DASHBOARD_FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search surveys...' },
  { key: 'status', label: 'Status', type: 'select', allLabel: 'All statuses', multi: true },
  { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
];

export const PAYMENTS_DASHBOARD_FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search payments...' },
  { key: 'daterange', label: 'Date', type: 'daterange' },
  { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
  { key: 'payment-type', label: 'Payment Type', type: 'select', allLabel: 'All types', multi: true },
  { key: 'payment-status', label: 'Status', type: 'select', allLabel: 'All statuses', multi: true },
  { key: 'plan-type', label: 'Plan Type', type: 'select', allLabel: 'All plan types', multi: true },
  { key: 'product', label: 'Product', type: 'select', allLabel: 'All products', multi: true, collapsibleGroups: true },
  { key: 'membership-type', label: 'Membership', type: 'select', allLabel: 'All memberships', multi: true },
  { key: 'package-type', label: 'Package', type: 'select', allLabel: 'All packages', multi: true },
  { key: 'doctor', label: 'Doctor', type: 'select', allLabel: 'All doctors', multi: true },
  { key: 'coordinator', label: 'Coordinator', type: 'select', allLabel: 'All coordinators', multi: true },
  { key: 'users', label: 'User', type: 'select', allLabel: 'All users', multi: true },
  leadSourceFilterConfig('Referral Source', 'All sources'),
];

export const INVENTORY_DASHBOARD_FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search products...' },
  { key: 'category', label: 'Category', type: 'select', allLabel: 'All categories', multi: true },
  { key: 'productType', label: 'Product Type', type: 'select', allLabel: 'All product types', multi: true },
  { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
  { key: 'status', label: 'Status', type: 'select', allLabel: 'All statuses', multi: true },
  { key: 'stockLevel', label: 'Stock Level', type: 'select', allLabel: 'All levels', multi: true },
];

/**
 * My Reports now lists every kind of report in one table, so its filters
 * describe properties a report HAS rather than which backend produced it. The
 * previous set asked users to choose between "on-demand" and "scheduled" as if
 * they were separate products, which is exactly the distinction people said they
 * did not care about.
 */
export const MY_REPORTS_FILTER_CONFIGS: FilterConfig[] = [
  // Searches name, sub-report AND filter summary — see MyReportsPage's haystack.
  // The page has always filtered on this key; it was missing from this list, so
  // the control that sets it was never rendered and the search was unreachable.
  { key: 'search',     label: 'Search',    type: 'search', allLabel: 'Search reports...' },
  { key: 'reportKind', label: 'Type',      type: 'select', allLabel: 'All types',   multi: true },
  { key: 'schedule',   label: 'Schedule',  type: 'select', allLabel: 'All',         multi: true },
  { key: 'status',     label: 'Status',    type: 'select', allLabel: 'All',         multi: true },
  { key: 'ownership',  label: 'Ownership', type: 'select', allLabel: 'All reports', multi: true },
];

/**
 * Where a report came from. Shown as a column and offered as a filter, but
 * deliberately not as tabs or groups: the kind changes what a row can DO, not
 * what it IS, and a user looking for last month's numbers should not have to
 * know which system produced them.
 */
export const REPORT_KIND_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'custom', label: 'Custom' },
  { value: 'ai', label: 'AI Assistant' },
];

export const REPORT_SCHEDULE_OPTIONS = [
  { value: 'recurring', label: 'Recurring' },
  { value: 'oneoff', label: 'One-off' },
];

export const REPORT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
];

/**
 * Ownership filter options for the My Reports list.
 *
 * Only some report kinds can be shared at all, so filtering to "Shared with me"
 * necessarily narrows to those. "Reports I've shared" is deliberately absent: it
 * needs the reverse of the share join and is not built.
 */
export const REPORT_OWNERSHIP_OPTIONS = [
  { value: 'mine', label: 'My reports' },
  { value: 'shared', label: 'Shared with me' },
];

export const CRM_DASHBOARD_FILTER_CONFIGS: FilterConfig[] = [
  { key: 'daterange', label: 'Date', type: 'daterange' },
  { key: 'lead-status', label: 'Lead Status', type: 'select', allLabel: 'All Statuses', multi: true },
  { key: 'lead-assign', label: 'Assign Status', type: 'select', allLabel: 'All', multi: true },
  leadSourceFilterConfig('Lead Source', 'All Sources'),
  { key: 'utm-source', label: 'UTM Source', type: 'select', allLabel: 'All UTM Sources', multi: true },
  { key: 'utm-campaign', label: 'Campaign', type: 'select', allLabel: 'All Campaigns', multi: true },
  { key: 'plan-type', label: 'Plan Type', type: 'select', allLabel: 'All Plan Types', multi: true },
  { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All Facilities', multi: true },
  { key: 'users', label: 'User', type: 'select', allLabel: 'All Users', multi: true },
  { key: 'groups', label: 'Group', type: 'select', allLabel: 'All Groups', multi: true },
];

export const PAYMENT_TYPE_OPTIONS = [
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'ach', label: 'ACH' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'gift_card', label: 'Gift Card' },
];

/**
 * Payment status filter options. Mirrors the legacy
 * `lib/filters/filter-payment-status.php` (Manually Entered, Pending,
 * Settled, Voided, Declined, Refund). Filter keys map to predicates over
 * the latest `payment_transactions_authnet.status` plus `payments.void`
 * in `PaymentsController::applyFilters`. Keep in sync with the badge
 * mapping in `paymentsShared.getPaymentStatusDisplay`.
 */
export const PAYMENT_STATUS_OPTIONS = [
  { value: '0', label: 'Manually Entered' },
  { value: '1', label: 'Pending' },
  { value: '2', label: 'Settled' },
  { value: '3', label: 'Voided' },
  { value: '4', label: 'Declined' },
  { value: '5', label: 'Refund' },
];

export const FORMS_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export const INVENTORY_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

// Mirrors the server-side stock ladder (InventoryController::stockStatusFor).
export const INVENTORY_STOCK_LEVEL_OPTIONS = [
  { value: 'out', label: 'Out of Stock' },
  { value: 'low', label: 'Low Stock' },
  { value: 'ok', label: 'In Stock' },
];

function defaultForConfig(config: FilterConfig): FilterValue {
  if (config.type === 'daterange') {
    // Resolved here so the filter has concrete dates immediately, and carried
    // as a preset so normalizeDateRanges() re-resolves it on every later load.
    // clearFilter() also routes through here, which is what makes "clear" on a
    // defaulted filter return to the default rather than to all-time.
    if (config.defaultPreset) {
      return { ...resolvePreset(config.defaultPreset), preset: config.defaultPreset };
    }
    return { from: '', to: '' };
  }
  if (config.type === 'range') return { min: config.min ?? 0, max: config.max ?? 120 };
  return [];
}

function defaultsForConfigs(configs: FilterConfig[]): Record<string, FilterValue> {
  return Object.fromEntries(configs.map((config) => [config.key, defaultForConfig(config)]));
}

/**
 * Recompute resolved from/to for any daterange filter that carries a preset
 * key. Mirrors legacy FiltersDateRange.php which re-derives the dates on
 * every page load so saved filters stay relative to "now". Stored values
 * are not mutated; only the in-memory shape consumers see is refreshed.
 */
function normalizeDateRanges(
  configs: FilterConfig[],
  values: Record<string, FilterValue>,
  now: Date,
): Record<string, FilterValue> {
  let next = values;
  for (const config of configs) {
    if (config.type !== 'daterange') continue;
    const v = values[config.key];
    if (!v || !isDateRange(v)) continue;

    // An empty range with NO preset means "nothing chosen", and for a config
    // that declares a default that means the DEFAULT, not all-time.
    //
    // This is also the migration path, and without it the default would never
    // reach anyone who matters: stored values override defaults in the merge
    // above, so every user who had already visited the page carries an empty
    // range in localStorage and would keep seeing all-time forever.
    //
    // It is what makes the 'all' preset load-bearing rather than decorative --
    // once empty means "default", an explicit key is the only way left to ask
    // for the whole history.
    if (config.defaultPreset && !v.preset && !v.from && !v.to) {
      const seeded = resolvePreset(config.defaultPreset, now);
      if (next === values) next = { ...values };
      next[config.key] = { ...v, ...seeded, preset: config.defaultPreset };
      continue;
    }

    if (!v.preset || !isDateRangePresetKey(v.preset)) continue;
    const resolved = resolvePreset(v.preset, now);
    if (resolved.from === v.from && resolved.to === v.to) continue;
    if (next === values) next = { ...values };
    next[config.key] = { ...v, from: resolved.from, to: resolved.to };
  }
  return next;
}

function numberValue(values: Record<string, FilterValue>, key: string): number | undefined {
  const v = values[key];
  const raw = isSelect(v) ? v[0] ?? '' : '';
  if (!raw) return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * Stored shape under `ftp-filters-<key>`. Older entries persisted only the
 * values map directly (without negations); we preserve that on read.
 */
type StoredFilterState =
  | Record<string, FilterValue>
  | { values: Record<string, FilterValue>; negations: FilterNegations };

function isWrappedState(
  state: StoredFilterState,
): state is { values: Record<string, FilterValue>; negations: FilterNegations } {
  return (
    typeof state === 'object' &&
    state !== null &&
    !Array.isArray(state) &&
    'values' in state &&
    'negations' in state
  );
}

function readValuesFrom(state: StoredFilterState): Record<string, FilterValue> {
  return isWrappedState(state) ? state.values : state;
}

/**
 * Migrate old camelCase filter keys to canonical legacy kebab-case keys.
 * Old localStorage entries may have stored values under the pre-unification keys.
 * This map is checked on read so old data self-heals without user intervention.
 */
const LEGACY_KEY_MIGRATION: Record<string, string> = {
  status: 'membership-status',
  referralSource: 'lead-source',
  tag: 'tags',
  createdDate: 'daterange',
  planType: 'plan-type',
  leadSource: 'lead-source',
  appointmentType: 'appt-type',
  membership: 'membership-type',
  stage: 'plan-stages',
  payType: 'payment-type',
  package: 'package-type',
  user: 'users',
  provider: 'doctor',
  type: 'appt-type',
};

/**
 * True when a stored value carries no active selection. `setFilter` seeds the
 * full default map on every write, so a canonical key is almost always present
 * but empty — which is why migration can't just test for key existence.
 */
function isBlankValue(value: FilterValue | undefined): boolean {
  if (value === undefined || value === null) return true;
  if (isSelect(value)) return value.length === 0;
  if (isDateRange(value)) return !value.from && !value.to;
  return false;
}

function migrateStoredKeys(values: Record<string, FilterValue>, configs: FilterConfig[]): Record<string, FilterValue> {
  const canonicalKeys = new Set(configs.map((c) => c.key));
  let migrated = false;
  let next = values;
  for (const [oldKey, newKey] of Object.entries(LEGACY_KEY_MIGRATION)) {
    if (canonicalKeys.has(oldKey)) continue;
    if (!(oldKey in next)) continue;
    if (!migrated) { next = { ...values }; migrated = true; }
    // Only overwrite the canonical key when it has no selection of its own, so
    // a real user choice always beats a stale legacy value. Either way the
    // legacy key is dropped: it can't be rendered or cleared in the sidebar,
    // but it would still be sent in filtersArray.
    if (!isBlankValue(next[oldKey]) && isBlankValue(next[newKey])) {
      next[newKey] = next[oldKey];
    }
    delete next[oldKey];
  }
  return next;
}

/**
 * Drop non-string entries from persisted select/search arrays. A past bug let
 * object option values get persisted (e.g. raw category rows on the inventory
 * page), which then crashed every render of the page until the user cleared
 * localStorage. Scrubbing on read self-heals those saved states.
 */
function sanitizeStoredValues(
  values: Record<string, FilterValue>,
  configs: FilterConfig[],
): Record<string, FilterValue> {
  let next = migrateStoredKeys(values, configs);
  let copied = next !== values;
  for (const [key, value] of Object.entries(next)) {
    if (!Array.isArray(value)) continue;
    if (value.every((entry) => typeof entry === 'string')) continue;
    if (!copied) { next = { ...next }; copied = true; }
    next[key] = value.filter((entry): entry is string => typeof entry === 'string');
  }
  return next;
}

function readNegationsFrom(state: StoredFilterState): FilterNegations {
  return isWrappedState(state) ? state.negations : {};
}

export function useLocalDashboardFilters(configs: FilterConfig[], storageKey?: string) {
  const defaultValues = useMemo(() => defaultsForConfigs(configs), [configs]);
  const defaultState = useMemo<StoredFilterState>(
    () => ({ values: defaultValues, negations: {} }),
    [defaultValues],
  );
  const [localState, setLocalState] = useState<StoredFilterState>(defaultState);
  const [storedState, setStoredState] = useLocalStorage<StoredFilterState>(
    storageKey ? `ftp-filters-${storageKey}` : '__unused-dashboard-filters',
    defaultState,
  );

  const activeState = storageKey ? storedState : localState;
  const setActiveState = useCallback(
    (updater: (current: StoredFilterState) => StoredFilterState) => {
      if (storageKey) setStoredState((current) => updater(current));
      else setLocalState((current) => updater(current));
    },
    [setStoredState, storageKey],
  );

  const values = useMemo(() => {
    const stored = sanitizeStoredValues(readValuesFrom(activeState), configs);
    const merged = { ...defaultValues, ...stored };
    return normalizeDateRanges(configs, merged, new Date());
  }, [configs, defaultValues, activeState]);

  const negations = useMemo<FilterNegations>(() => {
    const raw = readNegationsFrom(activeState);
    const migrated: FilterNegations = {};
    for (const [key, val] of Object.entries(raw)) {
      const canonical = LEGACY_KEY_MIGRATION[key] ?? key;
      migrated[canonical] = val;
    }
    return migrated;
  }, [activeState]);

  const setFilter = useCallback(
    (key: string, value: FilterValue) => {
      setActiveState((current) => {
        const currentValues = readValuesFrom(current);
        const currentNegations = readNegationsFrom(current);
        return {
          values: { ...defaultValues, ...currentValues, [key]: value },
          negations: currentNegations,
        };
      });
    },
    [defaultValues, setActiveState],
  );

  const setNegation = useCallback(
    (key: string, negate: boolean) => {
      setActiveState((current) => {
        const currentValues = readValuesFrom(current);
        const currentNegations = readNegationsFrom(current);
        const next = { ...currentNegations };
        if (negate) next[key] = true;
        else delete next[key];
        return {
          values: { ...defaultValues, ...currentValues },
          negations: next,
        };
      });
    },
    [defaultValues, setActiveState],
  );

  const clearFilter = useCallback(
    (key: string) => {
      const config = configs.find((item) => item.key === key);
      if (!config) return;
      setActiveState((current) => {
        const currentValues = readValuesFrom(current);
        const currentNegations = readNegationsFrom(current);
        const nextNegations = { ...currentNegations };
        delete nextNegations[key];
        return {
          values: { ...defaultValues, ...currentValues, [key]: defaultForConfig(config) },
          negations: nextNegations,
        };
      });
    },
    [configs, defaultValues, setActiveState],
  );

  return { values, negations, setFilter, setNegation, clearFilter };
}

export function useFacilityOverrideValues(values: Record<string, FilterValue>) {
  const localFacilityId = numberValue(values, 'facility');
  const effectiveFacilityId = useEffectiveFacilityId(localFacilityId);
  const effectiveFacilityIds = useEffectiveFacilityIds(selectValues(values, 'facility'));
  const isFacilityLocked = useIsFacilityLocked();

  const effectiveValues = useMemo(() => {
    if (!isFacilityLocked) return values;
    return {
      ...values,
      facility: effectiveFacilityIds,
    };
  }, [effectiveFacilityIds, isFacilityLocked, values]);

  return { effectiveValues, effectiveFacilityId, effectiveFacilityIds, isFacilityLocked };
}

export function selectValues(values: Record<string, FilterValue>, key: string): SelectValue {
  const value = values[key];
  return isSelect(value) ? value : [];
}
